import { useState } from 'react'
import Modal from './Modal'
import { track } from '../utils/analytics'
import type { GameState } from '../stores/gameStore'

interface GameOverModalProps {
  won: boolean
  draw: boolean
  difficulty: GameState['aiDifficulty']
  /** Total plies in the finished game; halved for the "your moves" count. */
  moveCount: number
  onRematch: () => void
  onBack: () => void
  onFeedback: () => void
}

const DIFFICULTY_LABELS: Record<GameState['aiDifficulty'], string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
  advanced: 'Advanced',
}

const SITE = 'https://www.chinesecheckers.xyz/'

export default function GameOverModal({
  won,
  draw,
  difficulty,
  moveCount,
  onRematch,
  onBack,
  onFeedback,
}: GameOverModalProps) {
  const [copied, setCopied] = useState(false)
  const label = DIFFICULTY_LABELS[difficulty]
  const myMoves = Math.ceil(moveCount / 2)

  const shareText = draw
    ? `I drew with the ${label} Chinese Checkers AI. Can you beat it? ${SITE}`
    : won
      ? `I beat the ${label} Chinese Checkers AI in ${myMoves} moves. Can you beat it? ${SITE}`
      : `The ${label} Chinese Checkers AI just beat me. Think you can do better? ${SITE}`

  const share = async () => {
    track('result_shared', { difficulty, won, moves: myMoves })
    // Native share sheet on mobile, clipboard everywhere else.
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Chinese Checkers', text: shareText, url: SITE })
        return
      } catch {
        /* dismissed — fall through to copying */
      }
    }
    try {
      await navigator.clipboard.writeText(shareText)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2400)
    } catch {
      /* clipboard blocked; nothing useful to do */
    }
  }

  return (
    <Modal
      title={draw ? 'Draw' : won ? 'You win' : `${label} AI wins`}
      actions={
        <>
          <button className="sidebar-primary" type="button" onClick={onRematch}>
            Rematch
          </button>
          <button className="sidebar-option" type="button" onClick={share}>
            {copied ? 'Copied!' : 'Share result'}
          </button>
          <button className="sidebar-option" type="button" onClick={onBack}>
            Main Menu
          </button>
        </>
      }
    >
      <p className="howto-lead">
        {draw
          ? `Neither side could finish against ${label}.`
          : won
            ? `You beat the ${label} AI in ${myMoves} moves.`
            : `${label} filled its home first — this one took ${myMoves} moves.`}
      </p>
      <p className="feedback-note result-feedback">
        <button className="link-button" type="button" onClick={onFeedback}>
          Feedback / Suggest a feature
        </button>
      </p>
    </Modal>
  )
}
